import { Component, OnInit, inject, effect, computed } from '@angular/core';
import { environment } from '../../environments/environment';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import Keycloak from 'keycloak-js';
import { RoleService } from '../auth/role-service';
import { loginOptions } from '../auth/login-options';
import { Role, PlateComponent, JjamesButtonComponent, findPlateCountry, normalizePlate, RegionSeparator } from 'shared-lib';
import { LicensePlateComponent } from './license-plate/license-plate.component';
import { CurrentParkingProcessComponent } from './license-plate/current-parking-process/current-parking-process.component';
import { JkuUserService } from '../jku-webshop/jku-user.service';
import { Router } from '@angular/router';
import { ParkerStateService } from '../services/parker-state.service';
import { ParkerLicenseDto } from '../../api/gen';

interface PlateRow {
  licensePlateId: string;
  fullNumber: string;
  euBand: string;
  countryCode: string;
  region: string;
  regionSeparator: RegionSeparator;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    CurrencyPipe,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    LicensePlateComponent,
    CurrentParkingProcessComponent,
    PlateComponent,
    JjamesButtonComponent
  ],
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss'
})
export class HomeComponent implements OnInit {
  private readonly keycloak = inject(Keycloak);
  private readonly roleService = inject(RoleService);
  private readonly jkuUser = inject(JkuUserService);
  private readonly router = inject(Router);
  readonly state = inject(ParkerStateService);

  readonly isAuthenticated = computed(() => !!this.keycloak.authenticated);

  readonly userFirstName = computed(() => {
    const acc = this.state.account();
    if (acc?.firstName) return acc.firstName;
    const token = this.keycloak.tokenParsed;
    return (token as any)?.given_name || (token as any)?.name || '';
  });

  readonly plateRows = computed<PlateRow[]>(() => {
    return this.state.myPlates().map(link => {
      const countryCode = link.licensePlate?.countryCode?.trim().toUpperCase() || 'AT';
      const norm = normalizePlate(link.licensePlate?.fullNumber);
      const country = findPlateCountry(countryCode);
      return {
        licensePlateId: link.licensePlate?.id || norm,
        fullNumber: norm,
        countryCode,
        euBand: country?.euBand ?? countryCode,
        region: link.licensePlate?.state?.trim().toUpperCase() || '',
        regionSeparator: country?.regionSeparator ?? 'none'
      };
    });
  });

  constructor() {
    effect(() => {
      if (this.jkuUser.isJkuSso()) {
        this.router.navigate(['/JKU-webshop'], { replaceUrl: true });
      }
    });
  }

  ngOnInit(): void {
    if (this.keycloak.authenticated) {
      this.state.refreshAll();
    }
  }

  login(): void {
    this.keycloak.login(loginOptions());
  }

  register(): void {
    this.router.navigate(['/register']);
  }

  goToActivity(): void {
    this.router.navigate(['/activity']);
  }

  goToPlates(): void {
    this.router.navigate(['/license-plates']);
  }

  goToRegisterPlate(): void {
    this.router.navigate(['/license-plates/register']);
  }

  goToEntitlements(): void {
    this.router.navigate(['/entitlements']);
  }

  onPayStay(parkingProcessId?: string | null): void {
    this.state.payStay(parkingProcessId);
  }

  protected readonly environment = environment;
}
